import React from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const BAR_WIDTH = 64
const BAR_GAP = 28
const PLOT_TOP = 36
const PLOT_HEIGHT = 170
const Y_AXIS_AREA = 42
const LABEL_AREA = 62
const VIEW_HEIGHT = PLOT_TOP + PLOT_HEIGHT + LABEL_AREA
const INSIDE_MIN_HEIGHT = 44
const MIN_PX_PER_BAR = 56
const LABEL_MAX_CHARS = 14
const INK = '#1A1A1A'
const GRID_VALUES = [0, 50, 100]

const truncate = (value) => {
  const label = String(value || '')
  return label.length > LABEL_MAX_CHARS
    ? `${label.slice(0, LABEL_MAX_CHARS - 1).trimEnd()}…`
    : label
}

const ResumoDgBarChart = ({ title, bars = [] }) => {
  const theme = useTheme()
  const plotWidth = bars.length * (BAR_WIDTH + BAR_GAP) + BAR_GAP
  const viewWidth = Y_AXIS_AREA + plotWidth
  const baseline = PLOT_TOP + PLOT_HEIGHT

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      {bars.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Sem dados para esta análise.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
          <svg
            role='img'
            aria-label={`${title}. Eixo vertical: taxa de prenhez em porcentagem. Eixo horizontal: grupo analisado.`}
            viewBox={`0 0 ${viewWidth} ${VIEW_HEIGHT}`}
            style={{
              width: '100%',
              maxWidth: viewWidth,
              minWidth: Y_AXIS_AREA + bars.length * MIN_PX_PER_BAR,
              display: 'block',
            }}
          >
            {GRID_VALUES.map((value) => {
              const y = baseline - (value / 100) * PLOT_HEIGHT
              return (
                <g key={value}>
                  <line
                    x1={Y_AXIS_AREA}
                    x2={viewWidth}
                    y1={y}
                    y2={y}
                    stroke={theme.palette.divider}
                    strokeDasharray={value === 0 ? undefined : '3 3'}
                  />
                  <text
                    x={Y_AXIS_AREA - 6}
                    y={y + 4}
                    textAnchor='end'
                    fontSize={11}
                    fill={theme.palette.text.secondary}
                  >
                    {value}%
                  </text>
                </g>
              )
            })}
            <line
              x1={Y_AXIS_AREA}
              x2={viewWidth}
              y1={baseline}
              y2={baseline}
              stroke={theme.palette.divider}
            />
            <text
              x={14}
              y={PLOT_TOP + PLOT_HEIGHT / 2}
              textAnchor='middle'
              fontSize={11}
              fill={theme.palette.text.secondary}
              transform={`rotate(-90 14 ${PLOT_TOP + PLOT_HEIGHT / 2})`}
            >
              Taxa de prenhez (%)
            </text>
            {bars.map((bar, index) => {
              const height =
                (Math.max(0, Math.min(100, bar.rate)) / 100) * PLOT_HEIGHT
              const x = Y_AXIS_AREA + BAR_GAP + index * (BAR_WIDTH + BAR_GAP)
              const top = baseline - height
              const center = x + BAR_WIDTH / 2
              const inside = height >= INSIDE_MIN_HEIGHT
              const placement = inside ? 'inside' : 'outside'
              const textProps = {
                x: center,
                textAnchor: 'middle',
                fontWeight: 700,
                fontSize: 13,
                fill: inside ? '#FFFFFF' : INK,
                'data-placement': placement,
              }

              return (
                <g key={`${bar.label}-${index}`}>
                  <title>{bar.label}</title>
                  {height > 0 && (
                    <rect
                      x={x}
                      y={top}
                      width={BAR_WIDTH}
                      height={height}
                      rx={3}
                      fill={theme.palette.primary.main}
                    />
                  )}
                  <text {...textProps} y={inside ? top + 18 : top - 20}>
                    {bar.rateLabel}
                  </text>
                  <text
                    {...textProps}
                    y={inside ? top + height / 2 + 5 : top - 5}
                  >
                    {bar.nLabel}
                  </text>
                  <text
                    x={center}
                    y={baseline + 18}
                    textAnchor='middle'
                    fontSize={12}
                    fill={theme.palette.text.secondary}
                  >
                    {truncate(bar.label)}
                  </text>
                </g>
              )
            })}
            <text
              x={Y_AXIS_AREA + plotWidth / 2}
              y={VIEW_HEIGHT - 4}
              textAnchor='middle'
              fontSize={11}
              fill={theme.palette.text.secondary}
            >
              Grupo analisado
            </text>
          </svg>
        </Box>
      )}
    </Box>
  )
}

export default ResumoDgBarChart
